import { EvmItemType, EvmOrderType } from '@/service/EVM/types';
import { type EvmOrderData, type EvmOrderParameters } from '@/service/api/types';
import { type Address, erc721Abi, type Hex, hexToBigInt, parseUnits } from 'viem';
import dayjs from 'dayjs';
import EvmProvider, { type EvmProviderInterface } from '../provider';
import { readContract, signTypedData, writeContract } from '@wagmi/core';
import { NULL_ADDRESS, NULL_HASH } from '@/service/EVM/consts';
import type { EvmChainId, EvmChainType } from '@/service/EVM/config/types';
import EvmSignature from '../signature';
import { EvmOrderEIPSchema } from '@/service/EVM/EIP712Schemas';

/**
 * @param chainMapById - object with map from the list of networks
 * @param signTypedData - function from wagmi, which is used for signing
 */
export interface EvmOfferInterface extends EvmProviderInterface {
    chainMapById: Record<EvmChainId, EvmChainType>;
    signTypedData: typeof signTypedData;
}

export class EvmOffer extends EvmProvider implements EvmOfferInterface {
    chainMapById;
    signTypedData;

    constructor({
        chainIds,
                    eventEmitter,
                    config,
                    chainMapById,
                    signTypedData
    }: EvmOfferInterface) {
        super({
            eventEmitter,
            chainIds,
            config
        });
        this.chainMapById = chainMapById;
        this.signTypedData = signTypedData;
    }

    async buildOffer({
        userAddress,
                         paramChainId,
                         value,
                         marketplaceContract,
                         contract,
                         identifier,
                         fees,
                         itemType,
                         endTime,
                         amount,
                         nftItemType,
                         erc20TokenAddress
    }: {
        paramChainId: EvmChainId;
        userAddress?: Address;
        value: number;
        marketplaceContract: ContractSettings;
        contract: Address;
        identifier: string;
        fees: AppWTFCollectionFees;
        itemType: EvmItemType;
        nftItemType: EvmItemType;
        erc20TokenAddress: Address;
        endTime: string;
        amount?: number;
    }) {
        if (!userAddress) throw new Error('User is not connected');

        const order = await this.prepareOffer({
            userAddress,
            paramChainId,
            value,
            fees,
            itemType,
            nftItemType,
            contract,
            identifier,
            endTime,
            amount,
            erc20TokenAddress
        });

        const signatureService = new EvmSignature(order, EvmOrderEIPSchema);

        const signature = await signatureService.create(this.config, {
            domain: {
                name: marketplaceContract.name,
                version: marketplaceContract.version,
                chainId: paramChainId,
                verifyingContract: marketplaceContract.address
            },
            primaryType: 'OrderComponents'
        });

        return { order, signature };
    }

    private async prepareOffer({
        userAddress,
                                   paramChainId,
                                   value,
                                   fees,
                                   itemType,
                                   contract,
                                   identifier,
                                   endTime,
                                   nftItemType,
                                   amount,
                                   erc20TokenAddress
    }: {
        paramChainId: EvmChainId;
        userAddress: Address;
        identifier: string;
        value: number;
        contract: Address;
        fees: AppWTFCollectionFees;
        itemType: EvmItemType;
        nftItemType: EvmItemType;
        erc20TokenAddress: Address;
        endTime: string;
        amount?: number;
    }): Promise<EvmOrderParameters> {
        if (!erc20TokenAddress) {
            throw new Error('Unsupported network');
        }

        const decimals = itemType === EvmItemType.ERC20
            ? await readContract(this.config, {
                address: erc20TokenAddress,
                chainId: paramChainId,
                functionName: 'decimals',
                abi: erc20Abi
            }) as number
            : 18;

        const userReceives = value - (value * fees.reduce((acc, item) => item.fee + acc, 0) / 100);

        return {
            offerer: userAddress,
            offer: [
                {
                    itemType,
                    token: erc20TokenAddress,
                    identifierOrCriteria: 0,
                    startAmount: Number(parseUnits(String(userReceives), decimals)),
                    endAmount: Number(parseUnits(String(userReceives), decimals))
                }
            ],
            consideration: [
                {
                    itemType: nftItemType,
                    token: contract,
                    identifierOrCriteria: Number(identifier),
                    startAmount: amount || 1,
                    endAmount: amount || 1,
                    recipient: userAddress
                },
                ...fees.map(({ fee, recipient }) => ({
                    itemType: itemType,
                    token: erc20TokenAddress,
                    identifierOrCriteria: 0,
                    startAmount: Number(parseUnits(String((value / 100) * fee), decimals)),
                    endAmount: Number(parseUnits(String((value / 100) * fee), decimals)),
                    recipient
                }))
            ],
            startTime: Math.floor(Date.now() / 1000),
            endTime: dayjs(endTime).unix(),
            orderType: EvmOrderType.PARTIAL_OPEN,
            zone: NULL_ADDRESS,
            zoneHash: NULL_HASH,
            salt: this.generateSalt(),
            conduitKey: NULL_HASH,
            counter: 0,
            totalOriginalConsiderationItems: fees.length + 1
        };
    }

    public async acceptOffer({
        paramChainId,
                                 order,
                                 userAddress
    }: {
        paramChainId: EvmChainId;
        order: EvmOrderData;
        userAddress: Address;
    }) {
        if (!order.parameters.offerer) {
            throw new Error('Invalid order');
        }

        const nftConsideration = order.parameters.consideration.find(item =>
            isEvmErc1155(item.itemType) || isEvmErc721(item.itemType)
        );

        if (!nftConsideration) {
            throw new Error('Invalid order: No NFT consideration found');
        }

        const isApproved = await readContract(this.config, {
            address: nftConsideration.token,
            abi: erc721Abi,
            functionName: 'isApprovedForAll',
            args: [userAddress, order.protocolAddress],
            chainId: paramChainId
        });

        if (!isApproved) {
            await writeContract(this.config, {
                address: nftConsideration.token,
                abi: erc721Abi,
                functionName: 'setApprovalForAll',
                args: [order.protocolAddress, true],
                chainId: paramChainId
            });
        }

        const prepareOrder = {
            parameters: {
                offerer: order.parameters.offerer,
                offer: order.parameters.offer.map(offerItem => ({
                    itemType: offerItem.itemType,
                    token: offerItem.token as Address,
                    identifierOrCriteria: BigInt(offerItem.identifierOrCriteria),
                    startAmount: BigInt(offerItem.startAmount),
                    endAmount: BigInt(offerItem.endAmount)
                })),
                consideration: order.parameters.consideration.map(considerationItem => ({
                    itemType: considerationItem.itemType,
                    token: considerationItem.token as Address,
                    identifierOrCriteria: BigInt(considerationItem.identifierOrCriteria),
                    startAmount: BigInt(considerationItem.startAmount),
                    endAmount: BigInt(considerationItem.endAmount),
                    recipient: considerationItem.recipient as Address
                })),
                startTime: BigInt(order.parameters.startTime),
                endTime: BigInt(order.parameters.endTime),
                orderType: order.parameters.orderType,
                zone: order.parameters.zone as Address,
                zoneHash: order.parameters.zoneHash as Hex,
                salt: hexToBigInt(order.parameters.salt as Hex),
                conduitKey: order.parameters.conduitKey as Hex,
                counter: BigInt(order.parameters.counter),
                totalOriginalConsiderationItems: BigInt(order.parameters.totalOriginalConsiderationItems)
            },
            signature: order.signature as Hex
        };

        try {
            return await writeContract(this.config, {
                address: order.protocolAddress,
                abi: revenlyAbi,
                functionName: 'fulfillOrder',
                args: [prepareOrder, NULL_HASH],
                chainId: paramChainId
            });
        }
        catch (e) {
            throw new Error('Transaction failed');
        }
    }
}
